import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react'
import { styled } from '@mui/system'
import WbsItemApi from '../../../../../lib/functions/wbsItem'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { Autocomplete, Popper } from '@mui/material'
import TextInput from '../../input/TextInput'
import _ from 'lodash'
import { WbsItemType } from '../../../../../domain/entity/WbsItemEntity'

const StyledAutocomplete = styled(Autocomplete)({
  width: '175px',
  minWidth: '175px',
  height: '30px',
  marginRight: '6px',
  '& .MuiAutocomplete-inputRoot': {
    height: '100%',
    paddingTop: '3px !important',
  },
}) as typeof Autocomplete

interface OwnProps {
  value?: string
  onChange: (newValue: string | undefined) => void
  types?: WbsItemType[]
  emptyOptionLabel?: string
}

type Props = OwnProps & WrappedComponentProps

interface WbsItemOption {
  uuid: string
  code: string
  displayName: string
}

const EMPTY_OPTION_UUID = '__empty__'

const buildEmptyOption = (
  emptyOptionLabel?: string
): WbsItemOption | undefined =>
  emptyOptionLabel
    ? { uuid: EMPTY_OPTION_UUID, code: '', displayName: emptyOptionLabel }
    : undefined

export const WbsItemSelect = injectIntl(
  ({ intl, value, onChange, types, emptyOptionLabel }: Props) => {
    const [rendered, setRendered] = React.useState(false)
    const [selected, setSelected] = useState<WbsItemOption | undefined>()
    const [options, setOptions] = useState<WbsItemOption[]>([])
    const [inputValue, setInputValue] = useState<string>('')

    // 選択された候補を即座に表示へ反映しておく。親の value 更新を待って表示を
    // 決めると、親が値を受け取るまでの間や受け取らなかった場合に表示と確定値が
    // ずれる。
    const onValueChanged = useCallback(
      (_, option: WbsItemOption | null) => {
        setSelected(option ?? undefined)
        setInputValue(option?.displayName ?? '')
        onChange(option?.uuid === EMPTY_OPTION_UUID ? undefined : option?.uuid)
      },
      [onChange]
    )
    const search = useCallback(
      async (v: string) => {
        const entities = await WbsItemApi.search(v, { types })
        const emptyOption = buildEmptyOption(emptyOptionLabel)
        setOptions(emptyOption ? [emptyOption, ...entities] : entities)
      },
      [types, emptyOptionLabel]
    )
    const fetch = useCallback(
      async (value: string) => {
        try {
          const entity = (await WbsItemApi.getDetail({ uuid: value })).json
          setSelected(entity)
          setInputValue(entity.displayName)
          const emptyOption = buildEmptyOption(emptyOptionLabel)
          setOptions(emptyOption ? [emptyOption, entity] : [entity])
        } catch (err) {
          onValueChanged(undefined, null)
          search('')
        }
      },
      [emptyOptionLabel]
    )
    const searchOnInputChanged = useCallback(_.debounce(search, 300), [types])
    // 入力しただけでは確定値を変更しない。候補を選ばずに文字を打った時点で
    // onChange(undefined) してしまうと、利用側が即座に保存対象へ反映する作りの
    // 場合に「対象を探そうとして1文字打っただけ」で対象が外れてしまう。
    const onInputChanged = useCallback(
      (event: SyntheticEvent, val: string) => {
        // If an event is issued from outside the UI (event = null), the previous value may be returned.
        if (!!event) {
          setInputValue(val)
          searchOnInputChanged(val)
        }
      },
      [searchOnInputChanged]
    )
    // 候補を選ばずに入力欄を離れた場合は、表示を確定値に戻す。
    const onBlur = useCallback(() => {
      setInputValue(selected?.displayName ?? '')
    }, [selected])

    // Search or fetch when the component is rendered.
    useEffect(() => {
      if (!rendered) {
        if (value) {
          fetch(value)
        } else {
          search(inputValue)
        }
        setRendered(true)
      }
    }, [rendered])

    // Fetch WbsItem when value has been changed.
    // value が外れた場合も表示を必ず同期させる(emptyOptionLabel が無い利用側で
    // 前の選択が表示に残り続けるのを防ぐ)。
    useEffect(() => {
      if (value) {
        fetch(value)
        return
      }
      const emptyOption = buildEmptyOption(emptyOptionLabel)
      setSelected(emptyOption)
      setInputValue(emptyOption?.displayName ?? '')
    }, [value, emptyOptionLabel])

    return (
      <StyledAutocomplete
        value={selected}
        options={options}
        size={'small'}
        onChange={onValueChanged}
        inputValue={inputValue}
        onInputChange={onInputChanged}
        onBlur={onBlur}
        getOptionLabel={option => option.displayName || ''}
        PopperComponent={props => {
          return <Popper {...props} placement="bottom-start" />
        }}
        isOptionEqualToValue={(option: WbsItemOption, value: WbsItemOption) => {
          return value && option.uuid === value.uuid
        }}
        renderInput={params => (
          <TextInput
            {...params}
            placeholder={intl.formatMessage({
              id: 'wbsItemSelect',
            })}
            variant="outlined"
            fullWidth={true}
          />
        )}
        clearOnBlur={false}
      />
    )
  }
)
